import * as ENVIRONMENTS from 'constants/environments'
import { env } from 'utils/env'
import { Box, Stack, Tooltip, styled } from '@mui/material'
import { totalCellCountInRuns } from 'components/cell-visualizations/startAnalysis/utils'
import { DeepcellPrimarySelect } from 'components/shared'
import ChartIcon from 'components/shared/icons/ChartIcon'
import DistributionToggleIcon from 'components/shared/icons/DistributionToggleIcon'
import DownloadToggleIcon from 'components/shared/icons/DownloadToggleIcon'
import { useState } from 'react'
import useEventManager from 'redux/slices/hooks/useEventsManager'
import {
  Run,
  getEmbeddingsId,
  getEmbeddingsURL,
  getImagesExportId,
  getImagesExportURL,
} from 'utils/api'
import { useAuth } from 'services/auth'
import useGlobalDownloadTrigger from 'utils/useGlobalDownloadTrigger'
import { generateFileName } from 'utils/fileNameGenerator'
import { isOnPrem } from 'config'
import PrimaryButton from './PrimaryButton'

interface RunDetailsActionBarProps {
  handleStartAnalysis: VoidFunction
  handleBrowseCells: VoidFunction
  onDownload: VoidFunction
  runIdString: string | undefined
  instrumentIdString?: string
  runStartTime?: number
  runEndTime?: number | undefined
  runStatus?: string
  runs?: Run[]
}

const ActionBarContainer = styled(Stack)({
  borderRadius: '4px',
  background: '#FFF',
  boxShadow:
    '0px 1px 12px 0px rgba(0, 0, 0, 0.12), 0px 4px 8px 0px rgba(0, 0, 0, 0.02), 0px 1px 4px 0px rgba(0, 0, 0, 0.01)',
  padding: '8px 6px',
  height: '48px',
})

const IconSx = {
  width: '32px',
  height: '32px',
  cursor: 'pointer',
  '&:hover > rect': { fill: '#a1eabc' },
}

const METABASE_QC_DASHBOARD_URL =
  'https://crude-gannet.metabaseapp.com/dashboard/496-single-run-qc-details?tab=431-overview&run_id='

const RunDetailsActionBar = ({
  handleStartAnalysis,
  handleBrowseCells,
  onDownload,
  runIdString,
  instrumentIdString,
  runStartTime,
  runEndTime,
  runStatus,
  runs,
}: RunDetailsActionBarProps): JSX.Element => {
  const { isInternalUser } = useAuth()
  const totalCellCount = totalCellCountInRuns(runs ?? [])
  const eventsManager = useEventManager()

  const { triggerAsyncDownload } = useGlobalDownloadTrigger()

  const [selectedOption, setSelectedOption] = useState('Select an export option')

  // Download embeddings
  const downloadEmbeddings = async () => {
    if (!runIdString) return

    const fileName = generateFileName('embeddings', runs ?? [], runIdString)
    eventsManager.sendEmbeddingsRequest(runIdString, totalCellCount)

    await triggerAsyncDownload({
      type: 'embeddings',
      fileName,
      initiateDownload: async () => {
        const response = await getEmbeddingsId({ run_ids: [runIdString] })
        return response.data.id
      },
      pollStatus: async (taskId) => {
        const statusResponse = await getEmbeddingsURL(Number(taskId))
        return {
          status: statusResponse.data.status === 'ready' ? 'ready' : 'pending',
          downloadUrl: statusResponse.data.download_url,
        }
      },
      pollInterval: 5000,
    })
  }

  // Export images
  const exportImages = async () => {
    if (!runIdString) return

    const fileName = generateFileName('images', runs ?? [], runIdString)

    await triggerAsyncDownload({
      type: 'images',
      fileName,
      initiateDownload: async () => {
        const response = await getImagesExportId({ run_ids: [runIdString] })
        return response.data.id
      },
      pollStatus: async (taskId) => {
        const statusResponse = await getImagesExportURL(Number(taskId))
        return {
          status: statusResponse.data.status === 'ready' ? 'ready' : 'pending',
          downloadUrl: statusResponse.data.download_url,
        }
      },
      pollInterval: 5000,
    })
  }

  const RUNS_ACTIONS_LIST = isInternalUser || isOnPrem
    ? ['Select an export option', 'Download Embeddings', 'Download Images']
    : ['Select an export option', 'Download Embeddings']

  // check to see if a run was imported into Axon - runs outside of REM-I
  const importedRun = runStatus === 'Imported'

  // check to see if only one run selected
  const onlyOneRunEnabled = runIdString && runIdString.indexOf(',') <= -1

  // determines the appropriate influx db environment based on the environment in which the App is running.
  const influxDBEnv =
    env.APP_ENV === ENVIRONMENTS.PRODUCTION ? 'InfluxDB' : 'InfluxDB-Staging'
  const toTime = runEndTime || 'now'
  const GRAFANA_URL = `https://grafana.deepcellbio.com/d/SxtAYdjnk/instrument-metrics?orgId=1&var-Controller=${instrumentIdString}&from=${runStartTime}&to=${toTime}&var-Source=${influxDBEnv}`


  const handleExportSelection = (value: string) => {
    if (value === 'Select an export option') {
      return
    }
    setSelectedOption(value)

    if (value === 'Download Embeddings') {
      downloadEmbeddings()
    } else if (value === 'Download Images') {
      exportImages()
    }
    setSelectedOption('Select an export option')
  }

  const StyledLink = styled('a')({
    textDecoration: 'none',
    color: 'inherit',
  })

  const StyledRunActionsSelect = styled(DeepcellPrimarySelect)({
    background: '#E3FCEE',
    height: '32px',
    border: '1px solid #5F55D1',
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: 'normal',
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: '10px',
    color: '#5F55D1',
    width: '100%',
    boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      background: '#a1eabc',
    },
  })

  return (
    <>
      <ActionBarContainer direction="row" justifyContent="space-between">
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={1}>
            {onlyOneRunEnabled && (
              <PrimaryButton onClick={handleBrowseCells} sx={{ width: '150px' }}>
                Browse Cells
              </PrimaryButton>
            )}
            <PrimaryButton sx={{ width: '150px' }} onClick={handleStartAnalysis}>
              Start Analysis
            </PrimaryButton>
            <StyledRunActionsSelect
              items={RUNS_ACTIONS_LIST}
              value={selectedOption}
              onChange={(e) => handleExportSelection(e.target.value as string)}
              onOpen={() => setSelectedOption('Select an export option')}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          {onlyOneRunEnabled && isInternalUser && !importedRun && (
            <Tooltip title="Run metrics in Grafana">
              <StyledLink target="_blank" href={GRAFANA_URL}>
                <ChartIcon sx={IconSx} />
              </StyledLink>
            </Tooltip>
          )}
          {onlyOneRunEnabled && !importedRun && isInternalUser && (
            <Tooltip title="QC Dashboard in Metabase">
              <StyledLink target="_blank" href={METABASE_QC_DASHBOARD_URL + runIdString}>
                <DistributionToggleIcon sx={IconSx} />
              </StyledLink>
            </Tooltip>
          )}
          <Box onClick={onDownload}>
            <Tooltip title="Download run report">
              <DownloadToggleIcon sx={IconSx} />
            </Tooltip>
          </Box>
        </Stack>
      </ActionBarContainer>

    </>
  )
}

export default RunDetailsActionBar
